import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, PenTool, Plus, Trash2, UserPlus } from 'lucide-react';
import { salvaDettagliVerbale } from '../../services/verbaliService';

export interface FirmeVerbale {
  presidente?: string;
  segretario?: string;
}

export interface PartecipanteEsterno {
  nome: string;
  ruolo?: string;
}

interface VerbaleDettagliProps {
  eventId: string;
  firme: FirmeVerbale;
  partecipantiEsterni: PartecipanteEsterno[];
  /** Nome e cognome dell'autore: Presidente proposto (e salvato) se il campo è vuoto. */
  presidentePredefinito?: string;
  onCambio?: (dettagli: { firme?: FirmeVerbale; partecipantiEsterni?: PartecipanteEsterno[] }) => void;
}

type Stato = 'fermo' | 'salvo' | 'salvato' | 'errore';

const CAMPO = 'w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

const pulisci = (elenco: PartecipanteEsterno[]) => elenco
  .map((p) => ({ nome: (p.nome || '').trim(), ruolo: (p.ruolo || '').trim() }))
  .filter((p) => p.nome)
  .map((p) => (p.ruolo ? { nome: p.nome, ruolo: p.ruolo } : { nome: p.nome }));

/** Firme (Presidente, Segretario verbalizzante) e partecipanti esterni, salvati a ogni modifica. */
const VerbaleDettagli: React.FC<VerbaleDettagliProps> = ({
  eventId,
  firme,
  partecipantiEsterni,
  presidentePredefinito,
  onCambio,
}) => {
  const [presidente, setPresidente] = useState(firme.presidente || presidentePredefinito || '');
  const [segretario, setSegretario] = useState(firme.segretario || '');
  const [partecipanti, setPartecipanti] = useState<PartecipanteEsterno[]>(
    partecipantiEsterni.map((p) => ({ nome: p.nome, ruolo: p.ruolo || '' }))
  );
  const [stato, setStato] = useState<Stato>('fermo');
  const salvatiRef = useRef({
    presidente: firme.presidente || '',
    segretario: firme.segretario || '',
    partecipanti: JSON.stringify(pulisci(partecipantiEsterni)),
  });

  const salva = async (dettagli: { firme?: FirmeVerbale; partecipantiEsterni?: PartecipanteEsterno[] }) => {
    setStato('salvo');
    try {
      await salvaDettagliVerbale(eventId, dettagli);
      if (dettagli.firme?.presidente !== undefined) salvatiRef.current.presidente = dettagli.firme.presidente;
      if (dettagli.firme?.segretario !== undefined) salvatiRef.current.segretario = dettagli.firme.segretario;
      if (dettagli.partecipantiEsterni) salvatiRef.current.partecipanti = JSON.stringify(dettagli.partecipantiEsterni);
      onCambio?.(dettagli);
      setStato('salvato');
    } catch {
      setStato('errore');
    }
  };

  // Presidente vuoto: si salva subito il nome dell'autore (lo legge anche il PDF).
  const predefinitoFatto = useRef(false);
  useEffect(() => {
    if (predefinitoFatto.current) return;
    predefinitoFatto.current = true;
    const predefinito = (presidentePredefinito || '').trim();
    if (!(firme.presidente || '').trim() && predefinito) {
      salva({ firme: { presidente: predefinito } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una volta sola, all'apertura
  }, []);

  const salvaFirma = (chiave: 'presidente' | 'segretario', valore: string) => {
    const pulito = valore.trim();
    if (pulito === salvatiRef.current[chiave]) return;
    salva({ firme: { [chiave]: pulito } });
  };

  const salvaPartecipanti = (elenco: PartecipanteEsterno[]) => {
    const puliti = pulisci(elenco);
    if (JSON.stringify(puliti) === salvatiRef.current.partecipanti) return;
    salva({ partecipantiEsterni: puliti });
  };

  const cambiaPartecipante = (indice: number, campo: 'nome' | 'ruolo', valore: string) =>
    setPartecipanti((prima) => prima.map((p, i) => (i === indice ? { ...p, [campo]: valore.slice(0, 120) } : p)));

  const togli = (indice: number) => {
    const elenco = partecipanti.filter((_, i) => i !== indice);
    setPartecipanti(elenco);
    salvaPartecipanti(elenco);
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800" aria-label="Firme e partecipanti">
      <div className="mb-3 flex items-center gap-2">
        <PenTool className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-900 dark:text-white">Firme e partecipanti</span>
        <span className="ml-auto text-xs text-slate-500 dark:text-gray-400" aria-live="polite">
          {stato === 'salvo' && <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />Salvataggio…</span>}
          {stato === 'salvato' && <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300"><Check className="h-3 w-3" aria-hidden="true" />Salvato</span>}
          {stato === 'errore' && <span className="text-red-600 dark:text-red-400">Non salvato, riprova</span>}
        </span>
      </div>

      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
        <div>
          <label htmlFor="verbale-presidente" className="mb-1 block text-xs font-medium text-slate-600 dark:text-gray-300">Presidente</label>
          <input
            id="verbale-presidente"
            value={presidente}
            maxLength={120}
            onChange={(e) => setPresidente(e.target.value)}
            onBlur={() => salvaFirma('presidente', presidente)}
            className={CAMPO}
          />
        </div>
        <div>
          <label htmlFor="verbale-segretario" className="mb-1 block text-xs font-medium text-slate-600 dark:text-gray-300">Segretario verbalizzante</label>
          <input
            id="verbale-segretario"
            value={segretario}
            maxLength={120}
            onChange={(e) => setSegretario(e.target.value)}
            onBlur={() => salvaFirma('segretario', segretario)}
            placeholder="Nome e cognome"
            className={CAMPO}
          />
        </div>
      </div>
      <p className="mt-1.5 text-xs text-slate-500 dark:text-gray-400">Compaiono in fondo al PDF, con lo spazio per la firma a mano.</p>

      <div className="mt-4 border-t border-slate-100 pt-3 dark:border-gray-700">
        <div className="mb-2 flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
          <span className="text-sm font-semibold text-slate-900 dark:text-white">Partecipanti esterni</span>
        </div>
        <p className="mb-2 text-xs text-slate-500 dark:text-gray-400">Presenti non invitati (ospiti, relatori): entrano nella sezione «Presenti».</p>

        {partecipanti.length > 0 && (
          <ul className="space-y-2">
            {partecipanti.map((p, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <input
                  value={p.nome}
                  onChange={(e) => cambiaPartecipante(i, 'nome', e.target.value)}
                  onBlur={() => salvaPartecipanti(partecipanti)}
                  placeholder="Nome e cognome"
                  aria-label={`Nome del partecipante ${i + 1}`}
                  className={`${CAMPO} min-w-0 flex-1`}
                />
                <input
                  value={p.ruolo || ''}
                  onChange={(e) => cambiaPartecipante(i, 'ruolo', e.target.value)}
                  onBlur={() => salvaPartecipanti(partecipanti)}
                  placeholder="Ruolo"
                  aria-label={`Ruolo del partecipante ${i + 1}`}
                  className={`${CAMPO} min-w-0 flex-1`}
                />
                <button
                  type="button"
                  onClick={() => togli(i)}
                  aria-label={`Togli ${p.nome || `il partecipante ${i + 1}`}`}
                  className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={() => setPartecipanti((prima) => (prima.length >= 200 ? prima : [...prima, { nome: '', ruolo: '' }]))}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-emerald-300 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/30"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Aggiungi partecipante
        </button>
      </div>
    </section>
  );
};

export default VerbaleDettagli;
